import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, push, set, update, remove } from 'firebase/database';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ReportAnalysis.css';

const DebtorLedger = () => {
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [viewingDebtor, setViewingDebtor] = useState(null);
  const [entries, setEntries] = useState([{
    id: Date.now(),
    date: new Date().toISOString().split('T')[0],
    debtorName: '',
    debtorContact: '',
    debitAmount: '',
    dueDate: '',
    referenceNo: '',
    description: ''
  }]);
  const [submitting, setSubmitting] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(0);

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    fetchDebtors();
  }, []);

  const fetchDebtors = async () => {
    try {
      const db = getDatabase();
      const debtorsRef = ref(db, 'HTAMS/expenses/debtorledger');
      const snapshot = await get(debtorsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const debtorsList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setDebtors(debtorsList);
      } else {
        setDebtors([]);
      }
    } catch (error) {
      console.error('Error fetching debtors:', error);
      toast.error('Failed to fetch debtor data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    
    // Validation for contact field
    if (name === 'debtorContact') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setEntries(prev => prev.map((entry, i) => 
          i === index ? { ...entry, [name]: numericValue } : entry
        ));
      }
      return;
    }

    // Validation for amount fields
    if (name === 'debitAmount') {
      const numericValue = value.replace(/[^\d.]/g, '');
      const parts = numericValue.split('.');
      const validValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
      setEntries(prev => prev.map((entry, i) => 
        i === index ? { ...entry, [name]: validValue } : entry
      ));
      return;
    }

    // Validation for reference number - alphanumeric only
    if (name === 'referenceNo') {
      const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setEntries(prev => prev.map((entry, i) => 
        i === index ? { ...entry, [name]: alphanumericValue } : entry
      ));
      return;
    }

    setEntries(prev => prev.map((entry, i) => 
      i === index ? { ...entry, [name]: value } : entry
    ));
  };

  const addNewEntry = () => {
    const newIndex = entries.length;
    setEntries(prev => [...prev, {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      debtorName: '',
      debtorContact: '',
      debitAmount: '',
      dueDate: '',
      referenceNo: '',
      description: ''
    }]);
    setExpandedEntry(newIndex);
  };

  const removeEntry = (index) => {
    if (entries.length > 1) {
      setEntries(prev => prev.filter((_, i) => i !== index));
      setExpandedEntry(index > 0 ? index - 1 : 0);
    } else {
      toast.warning('At least one entry is required');
    }
  };

  const toggleEntry = (index) => {
    setExpandedEntry(expandedEntry === index ? index : index);
  };

  const validateEntry = (entry, index) => {
    const entryNum = index + 1;
    
    if (!entry.date) {
      toast.error(`Entry ${entryNum}: Date is required`);
      return false;
    }
    
    if (!entry.debtorName || entry.debtorName.trim() === '') {
      toast.error(`Entry ${entryNum}: Debtor name is required`);
      return false;
    }
    
    if (entry.debtorContact && entry.debtorContact.trim()) {
      if (!/^\d{10}$/.test(entry.debtorContact.trim())) {
        toast.error(`Entry ${entryNum}: Debtor contact must be 10 digits`);
        return false;
      }
    }
    
    if (!entry.debitAmount || entry.debitAmount.trim() === '') {
      toast.error(`Entry ${entryNum}: Debit amount is required`);
      return false;
    }
    
    if (parseFloat(entry.debitAmount) <= 0) {
      toast.error(`Entry ${entryNum}: Debit amount must be greater than 0`);
      return false;
    }
    
    return true;
  };

  const resetForm = () => {
    setEntries([{
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      debtorName: '',
      debtorContact: '',
      debitAmount: '',
      dueDate: '',
      referenceNo: '',
      description: ''
    }]);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for all entries
    for (let i = 0; i < entries.length; i++) {
      if (!validateEntry(entries[i], i)) {
        setExpandedEntry(i);
        return;
      }
    }
    
    setSubmitting(true);

    try {
      const db = getDatabase();
      
      if (editingId) {
        // Update existing debtor (single entry)
        const entry = entries[0];
        const debtorData = {
          date: entry.date,
          debtorName: entry.debtorName,
          debtorContact: entry.debtorContact,
          debitAmount: parseFloat(entry.debitAmount),
          dueDate: entry.dueDate,
          referenceNo: entry.referenceNo,
          description: entry.description
        };

        const debtorRef = ref(db, `HTAMS/expenses/debtorledger/${editingId}`);
        await update(debtorRef, debtorData);
        toast.success('Debtor updated successfully!');
        setEditingId(null);
      } else {
        // Create new debtors (multiple entries)
        let successCount = 0;
        
        for (const entry of entries) {
          const debtorData = {
            date: entry.date,
            debtorName: entry.debtorName,
            debtorContact: entry.debtorContact,
            debitAmount: parseFloat(entry.debitAmount),
            dueDate: entry.dueDate,
            referenceNo: entry.referenceNo,
            description: entry.description
          };

          const debtorsRef = ref(db, 'HTAMS/expenses/debtorledger');
          const newDebtorRef = push(debtorsRef);
          await set(newDebtorRef, debtorData);
          successCount++;
        }
        
        toast.success(`${successCount} debtor ${successCount > 1 ? 'entries' : 'entry'} added successfully!`);
      }

      resetForm();
      fetchDebtors();
    } catch (error) {
      console.error('Error saving debtor:', error);
      toast.error('Failed to save debtor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (debtor) => {
    setEntries([{
      id: debtor.id,
      date: debtor.date,
      debtorName: debtor.debtorName,
      debtorContact: debtor.debtorContact,
      debitAmount: debtor.debitAmount.toString(),
      dueDate: debtor.dueDate,
      referenceNo: debtor.referenceNo,
      description: debtor.description
    }]);
    setEditingId(debtor.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete debtor "${name}"?`)) {
      try {
        const db = getDatabase();
        const debtorRef = ref(db, `HTAMS/expenses/debtorledger/${id}`);
        await remove(debtorRef);
        toast.success('Debtor deleted successfully!');
        fetchDebtors();
      } catch (error) {
        console.error('Error deleting debtor:', error);
        toast.error('Failed to delete debtor');
      }
    }
  };

  const handleView = (debtor) => {
    setViewingDebtor(debtor);
  };

  const closeViewModal = () => {
    setViewingDebtor(null);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const filteredDebtors = debtors.filter(debtor => {
    const matchesSearch = debtor.debtorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         debtor.debtorContact?.includes(searchTerm) ||
                         debtor.referenceNo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = (!dateFilter.start || new Date(debtor.date) >= new Date(dateFilter.start)) &&
                       (!dateFilter.end || new Date(debtor.date) <= new Date(dateFilter.end));
    return matchesSearch && matchesDate;
  });

  const totalReceivable = filteredDebtors.reduce((sum, debtor) => sum + (parseFloat(debtor.debitAmount) || 0), 0);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDebtors = filteredDebtors.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDebtors.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const exportToExcel = () => {
    if (filteredDebtors.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Prepare data for export
    const exportData = filteredDebtors.map((debtor, index) => ({
      'Sr No': index + 1,
      'Date': formatDate(debtor.date),
      'Debtor Name': debtor.debtorName || '-',
      'Contact': debtor.debtorContact || '-',
      'Debit Amount': debtor.debitAmount,
      'Due Date': formatDate(debtor.dueDate) || '-',
      'Reference No': debtor.referenceNo || '-',
      'Description': debtor.description || '-'
    }));

    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in values
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Debtor_Ledger_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredDebtors.length} entries successfully!`);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter]);

  if (loading) return <div className="loading">Loading debtor ledger...</div>;

  return (
    <div className="report-analysis-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="page-header">
        <h2>Debtor Ledger</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="add-btn"
            onClick={exportToExcel}
            style={{ background: '#28a745' }}
          >
            📥 Export to Excel
          </button>
          <button 
            className="add-btn"
            onClick={() => {
              if (showForm && editingId) {
                handleCancelEdit();
              } else {
                setShowForm(!showForm);
              }
            }}
          >
            {showForm ? '✕ Close' : '+ Add Debtor'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-container">
          <h3 className="form-title">{editingId ? 'Edit Debtor' : 'Add New Debtor'}</h3>
          <form onSubmit={handleSubmit} className="purchase-form">
            {entries.map((entry, index) => {
              const isExpanded = expandedEntry === index;
              return (
                <div 
                  key={entry.id} 
                  style={{ 
                    marginBottom: '10px', 
                    border: isExpanded ? '2px solid #2c5f2d' : '2px solid #e0e0e0', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div 
                    onClick={() => toggleEntry(index)}
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      padding: '10px 15px',
                      background: isExpanded ? '#2c5f2d' : '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <h4 style={{ margin: 0, color: isExpanded ? 'white' : '#2c5f2d', fontWeight: 'bold' }}>
                        Entry {index + 1}
                      </h4>
                      {entry.debtorName && (
                        <span style={{ fontSize: '14px', color: isExpanded ? '#d4edda' : '#666' }}>
                          {entry.debtorName}
                        </span>
                      )}
                      {entry.debitAmount && (
                        <span style={{ fontSize: '14px', color: isExpanded ? '#d4edda' : '#666', fontWeight: 'bold' }}>
                          ₹{entry.debitAmount}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {!editingId && entries.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeEntry(index);
                          }}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '5px 15px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Remove
                        </button>
                      )}
                      <span style={{ color: isExpanded ? 'white' : '#2c5f2d', fontSize: '20px', fontWeight: 'bold' }}>
                        {isExpanded ? '−' : '+'}
                      </span>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div style={{ padding: '15px' }}>
            <div className="form-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: '15px' }}>
              <div className="form-group">
                <label>Date <span className="required">*</span></label>
                <input
                  type="date"
                  name="date"
                  value={entry.date}
                  onChange={(e) => handleInputChange(e, index)}
                  required
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Debtor Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="debtorName"
                  value={entry.debtorName}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Enter debtor name"
                  required
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Debtor Contact</label>
                <input
                  type="text"
                  name="debtorContact"
                  value={entry.debtorContact}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Enter contact number"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Debit Amount <span className="required">*</span></label>
                <input
                  type="text"
                  name="debitAmount"
                  value={entry.debitAmount}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Enter debit amount"
                  required
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  name="dueDate"
                  value={entry.dueDate}
                  onChange={(e) => handleInputChange(e, index)}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Reference No</label>
                <input
                  type="text"
                  name="referenceNo"
                  value={entry.referenceNo}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Enter reference number"
                  className="form-control"
                />
              </div>

              <div className="form-group" style={{ gridColumn: 'span 1' }}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={entry.description}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Enter description or notes"
                  rows="3"
                  className="form-control"
                />
              </div>
            </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!editingId && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={addNewEntry}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  + Add Another Entry
                </button>
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                onClick={handleCancelEdit}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting}
                className="btn-submit"
              >
                {submitting ? 'Saving...' : (editingId ? 'Update Debtor' : `Save ${entries.length} Debtor${entries.length > 1 ? 's' : ''}`)}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters">
        <input
          type="text"
          placeholder="Search by debtor name, contact, or reference..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ flex: '2' }}
        />
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Start Date</label>
          <input
            type="date"
            value={dateFilter.start}
            onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
            className="date-input"
          />
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>End Date</label>
          <input
            type="date"
            value={dateFilter.end}
            onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
            className="date-input"
          />
        </div>
        
        {(searchTerm || dateFilter.start || dateFilter.end) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setDateFilter({ start: '', end: '' });
            }}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Receivable</h3>
          <p>₹{totalReceivable.toLocaleString()}</p>
        </div>
      </div>

      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Date</th>
              <th>Debtor Name</th>
              <th>Contact</th>
              <th>Debit Amount</th>
              <th>Due Date</th>
              <th>Reference No</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentDebtors.length > 0 ? (
              currentDebtors.map((debtor, index) => (
                <tr key={debtor.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{formatDate(debtor.date)}</td>
                  <td>{debtor.debtorName}</td>
                  <td>{debtor.debtorContact || '-'}</td>
                  <td>₹{parseFloat(debtor.debitAmount)?.toLocaleString()}</td>
                  <td>{formatDate(debtor.dueDate) || '-'}</td>
                  <td>{debtor.referenceNo || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn view-btn"
                        onClick={() => handleView(debtor)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(debtor)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(debtor.id, debtor.debtorName)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : null}
          </tbody>
        </table>
        
        {currentDebtors.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📗</div>
            <h3>No debtor entries found</h3>
            <p>Add debtor entries using the "Add Debtor" button above.</p>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewingDebtor && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Debtor Details</h3>
              <button className="modal-close" onClick={closeViewModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Date:</label>
                  <span>{formatDate(viewingDebtor.date)}</span>
                </div>
                <div className="detail-item">
                  <label>Debtor Name:</label>
                  <span>{viewingDebtor.debtorName}</span>
                </div>
                <div className="detail-item">
                  <label>Contact:</label>
                  <span>{viewingDebtor.debtorContact || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Debit Amount:</label>
                  <span className="highlight-amount">₹{parseFloat(viewingDebtor.debitAmount)?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Due Date:</label>
                  <span>{formatDate(viewingDebtor.dueDate) || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Reference No:</label>
                  <span>{viewingDebtor.referenceNo || 'N/A'}</span>
                </div>
                {viewingDebtor.description && (
                  <div className="detail-item full-width">
                    <label>Description:</label>
                    <span>{viewingDebtor.description}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredDebtors.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredDebtors.length)} of {filteredDebtors.length} entries
          </div>
          <div className="pagination">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            {getPageNumbers().map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`pagination-btn ${currentPage === number ? 'active' : ''}`}
              >
                {number}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebtorLedger;
